// CreateTip.js (updated with edit functionality)
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Container, Alert, Spinner } from 'react-bootstrap';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import BaseUrl from '../../ApiServices/BaseUrl';
import './CreateTips.css';

function CreateTip() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get tip ID from URL if editing
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    content: '',
    excerpt: '',
    section: '',
    status: 'draft',
    slug: '',
    meta_description: '',
    tags: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch tip data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchTipData();
    }
  }, [id]);

  const fetchTipData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BaseUrl}/api/tips/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch tip data');
      }

      const tipData = await response.json();

      setFormData({
        title: tipData.title || '',
        category: tipData.category || '',
        content: tipData.content || '',
        excerpt: tipData.excerpt || '',
        section: tipData.section || '',
        status: tipData.status || 'draft',
        slug: tipData.slug || '',
        meta_description: tipData.meta_description || '',
        tags: tipData.tags || ''
      });

    } catch (error) {
      console.error('Error fetching tip data:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Error loading tip: ' + error.message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      }).then(() => {
        navigate('/managetips');
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Auto-generate slug when title changes
    if (name === 'title' && !formData.slug) {
      const generatedSlug = generateSlug(value);
      setFormData(prev => ({
        ...prev,
        slug: generatedSlug
      }));
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Auto-generate slug if empty
      const finalFormData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title)
      };

      console.log('Submitting form data:', finalFormData);

      const url = isEditMode ? `${BaseUrl}/api/tips/${id}` : `${BaseUrl}/api/tips`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalFormData),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response received:', text.substring(0, 200));
        throw new Error('Server returned an invalid response. Please check if the server is running.');
      }

      const responseData = await response.json();
      console.log('Server response:', responseData);

      if (response.ok) {
        const successMessage = isEditMode
          ? 'Tip updated successfully!'
          : 'Tip created successfully!';

        // Show success alert with SweetAlert
        Swal.fire({
          title: 'Success!',
          text: successMessage,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#0056a3',
        }).then(() => {
          // Navigate back to ManageTips
          navigate('/managetips');
        });

        // Reset form only if creating new
        if (!isEditMode) {
          setFormData({
            title: '',
            category: '',
            content: '',
            excerpt: '',
            section: '',
            status: 'draft',
            slug: '',
            meta_description: '',
            tags: ''
          });
        }
      } else {
        console.error('Server error response:', responseData);
        throw new Error(responseData.error || `Failed to ${isEditMode ? 'update' : 'create'} tip: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} tip:`, error);

      // Show error alert with SweetAlert
      Swal.fire({
        title: 'Error!',
        text: `Error ${isEditMode ? 'updating' : 'creating'} tip: ` + error.message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <AdminNavbar />
        <Container className="manage-tips" style={{ marginTop: '100px' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      <Container className="manage-tips">
        {/* Header with Back Button */}
        <div className="Createtip-text">
          <h2 className="mb-0">
            {isEditMode ? 'Edit Tip' : 'Create New Tip'}
          </h2>
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/managetips')}
            className="back-btn"
          >
            Back to Manage Tips
          </Button>
        </div>

        <div className="form-box">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formTitle">
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="Enter tip title *"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formCategory">
                  <Form.Control
                    type="text"
                    name="category"
                    placeholder="Enter category *"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  />
                  <Form.Text className="text-muted">
                    e.g., business, technology, marketing, sales
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSection">
                  <Form.Control
                    type="text"
                    name="section"
                    placeholder="Enter section *"
                    value={formData.section}
                    onChange={handleInputChange}
                    required
                  />
                  <Form.Text className="text-muted">
                    e.g., sales-tips, business-advice, marketing-strategies
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formStatus">
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select status *</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="formExcerpt">
              <Form.Control
                as="textarea"
                rows={3}
                name="excerpt"
                placeholder="Short description of the tip *"
                value={formData.excerpt}
                onChange={handleInputChange}
                required
              />
              <Form.Text className="text-muted">
                A brief summary that will appear in tip listings.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formContent">
              <Form.Control
                as="textarea"
                rows={8}
                name="content"
                placeholder="Tip content *"
                value={formData.content}
                onChange={handleInputChange}
                required
              />
              <Form.Text className="text-muted">
                The main content of your tip. You can use HTML formatting if needed.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSlug">
                  <Form.Control
                    type="text"
                    name="slug"
                    placeholder="URL-friendly version of title"
                    value={formData.slug}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    Auto-generated from title if empty. Use hyphens instead of spaces.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formTags">
                  <Form.Control
                    type="text"
                    name="tags"
                    placeholder="Comma-separated tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                  />
                  <Form.Text className="text-muted">
                    e.g., business-growth,sales-techniques,marketing-tips
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="formMetaDescription">
              <Form.Control
                as="textarea"
                rows={3}
                name="meta_description"
                placeholder="SEO meta description"
                value={formData.meta_description}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                This will appear in search engine results. Recommended: 150-160 characters.
              </Form.Text>
            </Form.Group>

 <div className="createtips-buttons">
  <Button
    variant="primary"
    type="submit"
    className="createtips-submit-btn"
    disabled={isSubmitting}
  >
    {isSubmitting ? (
      <>
        <Spinner animation="border" size="sm" className="me-2" />
        {isEditMode ? 'Updating...' : 'Publishing...'}
      </>
    ) : (
      isEditMode ? 'Update Tip' : 'Publish Tip'
    )}
  </Button>

  <Button
    variant="outline-secondary"
    onClick={() => navigate('/managetips')}
    disabled={isSubmitting}
    className="cancel-btn"
  >
    Cancel
  </Button>
</div>



          </Form>
        </div>
      </Container>
    </>
  );
}

export default CreateTip;